import React from 'react';
import { Table, UncontrolledTooltip } from 'reactstrap';
import { AvField } from 'availity-reactstrap-validation';
import {
  formatEffectiveDueDate,
  formatAlertSendDate,
  formatPenaltyApplyDate,
  formatPenaltyAmount
} from '../helpers/quarterDates';

const ALERT_DAY_TOOLTIP_ID = 'quarter-fees-alert-day-help';
const ALERT_DAY_TOOLTIP_TEXT =
  'Number of days before the installment due date (last day of the month). Parents receive a pre-due fee reminder email on that date.';

const PENALTY_DAY_TOOLTIP_ID = 'quarter-fees-penalty-day-help';
const PENALTY_DAY_TOOLTIP_TEXT =
  'Number of grace days after the due date before a late fee is applied. For example, if the due date is the 10th and Penalty Day is 5, the penalty starts from the 15th.';

const getMonthInputValue = (month, useIsoMonthValue) => {
  if (!month) return '';
  if (useIsoMonthValue) {
    return new Date(month).toISOString().slice(0, 7);
  }
  return month;
};

const QuarterFeesTable = ({
  quarters,
  setQuarters,
  dummyQuarter = false,
  useIsoMonthValue = false,
  ceilBaseFee = false,
  penaltyMode = 'daily_flat',
  dueDay = 10
}) => {
  const updateQuarter = (index, field, value) => {
    const updatedQuarters = [...quarters];
    updatedQuarters[index] = { ...updatedQuarters[index], [field]: value };
    setQuarters(updatedQuarters);
  };

  const quarterLabel = (index) => {
    if (dummyQuarter) return 'Dummy';
    return `Q${index + 1}`;
  };

  return (
    <div className="table-responsive mb-3">
      <Table bordered size="sm" className="mb-0">
        <thead>
          <tr>
            <th style={{ width: '72px' }}>Quarter</th>
            <th style={{ minWidth: '160px' }}>Month</th>
            <th style={{ minWidth: '100px' }}>Quarter Amount</th>
            <th style={{ minWidth: '110px' }}>
              Alert Day{' '}
              <i
                className="mdi mdi-information-outline text-muted"
                id={ALERT_DAY_TOOLTIP_ID}
                style={{ cursor: 'help' }}
              />
              <UncontrolledTooltip placement="top" target={ALERT_DAY_TOOLTIP_ID}>
                {ALERT_DAY_TOOLTIP_TEXT}
              </UncontrolledTooltip>
            </th>
            <th style={{ minWidth: '100px' }}>
              Penalty Day{' '}
              <i
                className="mdi mdi-information-outline text-muted"
                id={PENALTY_DAY_TOOLTIP_ID}
                style={{ cursor: 'help' }}
              />
              <UncontrolledTooltip placement="top" target={PENALTY_DAY_TOOLTIP_ID}>
                {PENALTY_DAY_TOOLTIP_TEXT}
              </UncontrolledTooltip>
            </th>
            <th style={{ minWidth: '90px' }}>{penaltyMode === 'daily_flat' ? 'Penalty/Day (₹)' : 'Penalty %'}</th>
          </tr>
        </thead>
        <tbody>
          {quarters.map((quarter, index) => {
            const effectiveDueDate = formatEffectiveDueDate(quarter?.month, dueDay);
            const alertSendDate = formatAlertSendDate(
              quarter?.month,
              quarter?.alert_day,
              dueDay
            );
            const penaltyApplyDate = formatPenaltyApplyDate(
              quarter?.month,
              quarter?.penalty_day
            );
            const baseFeeValue = ceilBaseFee
              ? Math.ceil(quarter?.base_fee)
              : quarter?.base_fee;
            const penaltyAmountLabel = formatPenaltyAmount(
              baseFeeValue,
              quarter?.penalty_percentage
            );

            return (
              <tr key={`quarter-row-${index}`}>
                <td className="align-middle font-weight-medium">
                  {quarterLabel(index)}
                </td>
                <td>
                  <AvField
                    name={`quarter${index}.month`}
                    placeholder="Month"
                    type="month"
                    value={getMonthInputValue(quarter?.month, useIsoMonthValue)}
                    className="form-control form-control-sm"
                    onChange={(e) => updateQuarter(index, 'month', e.target.value)}
                    validate={{
                      required: {
                        value: true,
                        errorMessage: `Quarter ${index + 1} month is required`
                      }
                    }}
                    id={`quarter-${index + 1}-month`}
                  />
                  {effectiveDueDate ? (
                    <small className="text-muted d-block mt-1">
                      Due: {effectiveDueDate}
                    </small>
                  ) : null}
                </td>
                <td>
                  <AvField
                    name={`quarter${index}.base_fee`}
                    placeholder="Quarter Amount"
                    type="number"
                    className="form-control form-control-sm"
                    value={baseFeeValue}
                    onChange={(e) => updateQuarter(index, 'base_fee', e.target.value)}
                    validate={{
                      required: {
                        value: true,
                        errorMessage: `Quarter ${index + 1} amount is required`
                      }
                    }}
                    id={`quarter-${index + 1}-base-fee`}
                  />
                </td>
                <td>
                  <AvField
                    name={`quarter${index}.alert_day`}
                    placeholder="Days"
                    type="number"
                    className="form-control form-control-sm"
                    value={quarter?.alert_day}
                    onChange={(e) => updateQuarter(index, 'alert_day', e.target.value)}
                    validate={{
                      required: {
                        value: true,
                        errorMessage: `Quarter ${index + 1} alert day is required`
                      },
                      min: {
                        value: 1,
                        errorMessage: 'Minimum alert day must be 1'
                      },
                      max: {
                        value: 29,
                        errorMessage: 'Maximum alert day must be 29'
                      }
                    }}
                  />
                  {alertSendDate ? (
                    <small className="text-muted d-block mt-1">
                      Alert: {alertSendDate}
                    </small>
                  ) : null}
                </td>
                <td>
                  <AvField
                    name={`quarter${index}.penalty_day`}
                    placeholder="Days"
                    type="number"
                    className="form-control form-control-sm"
                    value={quarter?.penalty_day}
                    onChange={(e) => updateQuarter(index, 'penalty_day', e.target.value)}
                    validate={{
                      required: {
                        value: true,
                        errorMessage: `Quarter ${index + 1} penalty day is required`
                      },
                      min: {
                        value: 0,
                        errorMessage: 'Minimum penalty day must be 0'
                      },
                      max: {
                        value: 365,
                        errorMessage: 'Maximum penalty day must be 365'
                      }
                    }}
                    id={`quarter-${index + 1}-penalty-day`}
                  />
                  {penaltyApplyDate ? (
                    <small className="text-muted d-block mt-1">
                      From: {penaltyApplyDate}
                    </small>
                  ) : null}
                </td>
                <td>
                  <AvField
                    name={`quarter${index}.penalty_percentage`}
                    placeholder="%"
                    type="number"
                    className="form-control form-control-sm"
                    value={quarter?.penalty_percentage}
                    onChange={(e) =>
                      updateQuarter(index, 'penalty_percentage', e.target.value)
                    }
                    validate={{
                      required: {
                        value: true,
                        errorMessage: `Quarter ${
                          index + 1
                        } penalty percentage is required`
                      }
                    }}
                    id={`quarter-${index + 1}-penalty-percentage`}
                  />
                  {penaltyMode === 'daily_flat' ? (
                    quarter?.penalty_percentage ? (
                      <small className="text-muted d-block mt-1">
                        ₹{quarter.penalty_percentage}/day
                      </small>
                    ) : null
                  ) : penaltyAmountLabel ? (
                    <small className="text-muted d-block mt-1">
                      Amount: {penaltyAmountLabel}
                    </small>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

export default QuarterFeesTable;
